import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    IonButton, IonCol, IonContent, IonGrid, IonHeader, IonItem, IonLabel,
    IonPage, IonRow, IonSelect, IonSelectOption, IonTextarea, IonTitle, IonToggle, IonToolbar
} from "@ionic/react";
import { AcaGetOpportunityResponseOpportunity } from "../types";

type Reason = { id: number; description: string; status: "ACTIVE" | "INACTIVE" };

const OTHER_LABEL = "Other Suspicious Activity";

const ModalComplianceFlag = ({dismiss, complianceReasons, opportunity, canSet, canUnset,}: {
    dismiss: (data?: { flag: boolean; reasonId?: number | null; reasonNote?: string | null } | null, role?: string) => void;
    opportunity: AcaGetOpportunityResponseOpportunity;
    complianceReasons: Reason[];
    canSet: boolean;   // tier2 or manager
    canUnset: boolean; // manager
}) => {
    const modal = useRef<HTMLIonModalElement>(null);

    // Initialize values
    const [flag, setFlag] = useState<boolean>(opportunity.complianceFlag);
    const [reasonId, setReasonId] = useState<number | null | undefined>(opportunity.complianceReasonId ?? null);
    const [reasonNote, setReasonNote] = useState<string>(opportunity.complianceReasonText ?? "");

    // keep state in sync on change
    useEffect(() => {
        setFlag(opportunity.complianceFlag);
        setReasonId(opportunity.complianceReasonId ?? null);
        setReasonNote(opportunity.complianceReasonText ?? "");
    }, [opportunity.complianceFlag, opportunity.complianceReasonId, opportunity.complianceReasonText]);

    const selectedReason = useMemo(
        () => complianceReasons.find(r => r.id === (reasonId ?? -1)),
        [complianceReasons, reasonId]
    );
    const isOther = selectedReason?.description === OTHER_LABEL;

    const toggleAllowed = (next: boolean) => {
        if (!next && flag && !canUnset) return false;
        if (next && !flag && !canSet) return false;
        return true;
    };

    const onToggle = (checked: boolean) => {
        if (!toggleAllowed(checked)) return;
        setFlag(checked);
        // clear fields
        if (!checked) {
            setReasonId(null);
            setReasonNote("");
        }
    };

    const isValid = useMemo(() => {
        if (!flag) return true;
        if (reasonId == null) return false;
        if (isOther && !reasonNote.trim()) return false;
        return true;
    }, [flag, reasonId, isOther, reasonNote]);

    return (
        <IonPage ref={modal}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Compliance Flag</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
                <IonGrid>

                    <IonRow className="ion-align-items-center">
                        <IonCol size="6"><b>Flag this opportunity?</b></IonCol>
                        <IonCol size="6" className="ion-text-right">
                            <IonToggle checked={flag} onIonChange={(e) => onToggle(e.detail.checked)} />
                        </IonCol>
                    </IonRow>

                    <IonRow>
                        <IonCol size="12">
                            <IonItem>
                                <IonLabel>Reason</IonLabel>
                                <IonSelect
                                    disabled={!flag}
                                    value={reasonId ?? undefined}
                                    onIonChange={e => setReasonId(Number(e.detail.value))}
                                    interface="popover"
                                >
                                    {complianceReasons.map(r => (
                                        <IonSelectOption key={r.id} value={r.id}>{r.description}</IonSelectOption>
                                    ))}
                                </IonSelect>
                            </IonItem>
                        </IonCol>
                    </IonRow>

                    {flag && isOther && (
                        <IonRow>
                            <IonCol size="12">
                                <IonTextarea
                                    label="Notes (required for Other)"
                                    labelPlacement="floating"
                                    value={reasonNote}
                                    onIonChange={(e) => setReasonNote(e.detail.value || "")}
                                    rows={5}
                                />
                            </IonCol>
                        </IonRow>
                    )}
                </IonGrid>

                <div className="ion-text-right" style={{ marginTop: 16 }}>
                    <IonButton fill="outline" onClick={() => dismiss(null, "cancel")}>
                        Cancel
                    </IonButton>
                    <IonButton
                        color="danger"
                        strong
                        disabled={!isValid}
                        onClick={() => {
                            if (!flag) {
                                return dismiss({ flag: false }, "confirm");
                            }
                            return dismiss(
                                {
                                    flag: true,
                                    reasonId: reasonId!,
                                    reasonNote: isOther ? reasonNote.trim() : null
                                },
                                "confirm"
                            );
                        }}
                    >
                        Confirm
                    </IonButton>
                </div>
            </IonContent>
        </IonPage>
    );
};

export default ModalComplianceFlag;